// Cluster sémantique « complément de revenu » — contenus éditoriaux honnêtes,
// chaque page a une intention de recherche distincte (pas de cannibalisation).
// Aucune promesse de revenus garantis.

export const RESOURCE_PAGES = {
  "complement-de-revenu": {
    slug: "complement-de-revenu",
    path: "/complement-de-revenu",
    pillar: true,
    breadcrumb: "Complément de revenu",
    title: "Complément de revenu : comment en créer un durablement (2026)",
    description:
      "Comment se créer un complément de revenu durable ? Définition, méthodes concrètes, profils concernés et première étape. Guide clair, sans promesse de gains garantis.",
    h1: "Complément de revenu : comment en créer un, concrètement",
    intro:
      "Un complément de revenu est une rentrée d'argent qui vient s'ajouter à votre revenu principal (salaire, pension, bourse). On peut le développer à son rythme, souvent depuis chez soi, en consacrant quelques heures par semaine à une activité qui génère des ventes ou des prestations. Il n'existe pas de revenu automatique ou garanti : ce que vous obtenez dépend du temps investi et des ventes réellement réalisées.",
    sections: [
      {
        h: "Qu'est-ce qu'un complément de revenu ?",
        p: "C'est une source d'argent secondaire, en plus de votre activité principale. Elle peut être ponctuelle (quelques ventes) ou régulière (une petite activité développée dans la durée). L'objectif est d'augmenter vos revenus mensuels sans forcément quitter votre emploi.",
      },
      {
        h: "Pourquoi chercher un revenu complémentaire ?",
        list: [
          "Arrondir ses fins de mois et absorber les dépenses imprévues.",
          "Financer un projet (voyage, épargne, études des enfants).",
          "Tester une activité entrepreneuriale sans prendre le risque de quitter son emploi.",
          "Gagner en autonomie financière, à son propre rythme.",
        ],
      },
      {
        h: "Quelles activités pour un complément de revenu ?",
        p: "Les pistes sont nombreuses : revente de produits, prestations de services, création de contenu, petits travaux à domicile. Chez Ozentya, la voie proposée est la revente de produits de beauté : vous recevez un pack de produits, vous les présentez autour de vous et en ligne, puis vous les revendez. Votre gain correspond à la marge entre votre prix d'achat et votre prix de revente.",
      },
      {
        h: "Combien de temps faut-il y consacrer ?",
        p: "Tout dépend de votre objectif. Quelques heures par semaine suffisent pour démarrer et réaliser ses premières ventes auprès de son entourage. Plus vous êtes régulier(ère), plus vous construisez une clientèle fidèle. La régularité compte davantage que le volume horaire ponctuel.",
      },
      {
        h: "Par où commencer ?",
        list: [
          "Clarifiez votre objectif (montant visé, temps disponible).",
          "Choisissez une activité qui vous plaît réellement : vous en parlerez plus naturellement.",
          "Commencez petit, testez, puis ajustez selon vos premiers retours.",
          "Soyez transparent(e) : ne promettez jamais de résultats, partagez des faits.",
        ],
      },
      {
        h: "Comment gagner un complément de revenu en ligne ?",
        p: "Internet élargit vos possibilités : vente de produits en ligne, création de contenu, affiliation, ou présentation de vos produits sur les réseaux sociaux. L'avantage : vous touchez bien plus de personnes que votre seul entourage. Chez Ozentya, vous combinez les deux — vente autour de vous ET en ligne via vos réseaux.",
      },
      {
        h: "Un complément de revenu à côté d'un emploi : est-ce possible ?",
        p: "Oui, c'est même le cas le plus fréquent. Un complément de revenu se développe en parallèle d'un emploi salarié, le soir ou le week-end. Vérifiez les clauses de votre contrat (exclusivité, non-concurrence) et déclarez vos revenus (le statut de micro-entrepreneur simplifie les démarches en France).",
      },
      {
        h: "Quel complément de revenu demande peu de temps ?",
        p: "Les activités qui se gèrent par créneaux courts sont les plus compatibles avec un emploi du temps chargé : revente de produits, petites prestations ponctuelles, location d'un bien. La revente est particulièrement souple car vous choisissez quand présenter et expédier vos produits.",
      },
      {
        h: "Avantages et limites des principales solutions",
        list: [
          "Revente de produits : faible coût de départ, flexible — mais demande de la régularité pour fidéliser.",
          "Freelance / prestations : bien rémunéré si vous avez une compétence — mais chronophage et irrégulier au début.",
          "Location (logement, matériel) : revenu passif — mais nécessite un bien à louer.",
          "Sondages / micro-tâches : accessibles à tous — mais très faible rentabilité.",
          "Affiliation / contenu : fort potentiel à terme — mais lent et nécessite une audience.",
        ],
      },
    ],
    faq: [
      {
        q: "Quel complément de revenu choisir ?",
        a: "Choisissez selon votre temps disponible, votre budget de départ et vos compétences. Pour démarrer vite avec peu de moyens, la revente de produits ou la vente d'objets est accessible ; pour valoriser une expertise, le freelance est plus rémunérateur.",
      },
      {
        q: "Comment gagner un complément de revenu en ligne ?",
        a: "En vendant des produits en ligne, en créant du contenu, via l'affiliation ou en présentant votre activité sur les réseaux sociaux. L'intérêt est de toucher une audience bien plus large que votre entourage immédiat.",
      },
      {
        q: "Comment avoir un complément de revenu rapidement ?",
        a: "Le plus rapide est de partir d'une activité à faible coût de démarrage, comme la revente de produits auprès de votre entourage. Les premières ventes peuvent arriver vite, mais aucun délai ni montant n'est garanti : cela dépend de vos efforts et de votre réseau.",
      },
      {
        q: "Peut-on avoir un complément de revenu sans quitter son emploi ?",
        a: "Oui. Un complément de revenu se développe généralement en parallèle d'un emploi, le soir ou le week-end, selon le temps dont vous disposez. Vérifiez simplement les clauses de votre contrat de travail et vos obligations déclaratives.",
      },
      {
        q: "Un complément de revenu est-il garanti ?",
        a: "Non. Toute activité comporte une part d'incertitude. Les revenus dépendent des ventes réellement réalisées. Méfiez-vous des offres promettant de l'« argent facile » ou des gains automatiques.",
      },
    ],
    related: ["revenu-complementaire", "activite-complementaire", "activite-a-domicile", "arrondir-ses-fins-de-mois"],
    articles: ["trouver-un-complement-de-revenu", "complement-de-revenu-par-ou-commencer", "10-idees-revenu-complementaire"],
  },

  "revenu-complementaire": {
    slug: "revenu-complementaire",
    path: "/revenu-complementaire",
    breadcrumb: "Revenu complémentaire",
    title: "Revenu complémentaire : solutions concrètes selon votre situation",
    description:
      "Quelles solutions pour générer un revenu complémentaire selon votre profil (salarié, étudiant, parent, retraité) ? Idées réalistes et première étape, sans gains garantis.",
    h1: "Revenu complémentaire : quelles solutions selon votre situation",
    intro:
      "Générer un revenu complémentaire consiste à ajouter une rentrée d'argent régulière ou ponctuelle à votre revenu principal. La bonne solution dépend surtout du temps dont vous disposez et de ce qui vous motive. Voici des pistes réalistes selon les profils, sans promesse de résultats.",
    sections: [
      {
        h: "Revenu complémentaire pour un salarié",
        p: "En tant que salarié, vous pouvez développer une petite activité le soir ou le week-end. La revente de produits est adaptée car elle se gère par créneaux courts. Pensez à vérifier votre contrat (clause d'exclusivité) et vos obligations déclaratives.",
      },
      {
        h: "Revenu complémentaire pour un étudiant",
        p: "Un étudiant recherche de la flexibilité. Une activité de revente permet de travailler selon son emploi du temps, sans horaires imposés, et de se constituer une première expérience commerciale.",
      },
      {
        h: "Revenu complémentaire pour un parent au foyer",
        p: "Depuis chez soi, en s'organisant autour de la vie de famille, il est possible de présenter et revendre des produits via son entourage et les réseaux sociaux, à son rythme.",
      },
      {
        h: "Revenu complémentaire pour un retraité",
        p: "Pour compléter une pension, une activité souple et peu exigeante physiquement, comme la revente, peut convenir. Renseignez-vous sur l'impact éventuel sur votre pension.",
      },
    ],
    faq: [
      {
        q: "Comment générer un revenu complémentaire à côté de son travail ?",
        a: "En choisissant une activité qui se gère par créneaux courts et flexibles. La revente de produits, par exemple, se développe le soir et le week-end, auprès de son entourage puis en ligne.",
      },
      {
        q: "Quel revenu complémentaire peut-on espérer ?",
        a: "Il n'existe pas de montant type : tout dépend du nombre de ventes réalisées et du temps investi. Nous ne garantissons aucun revenu.",
      },
    ],
    related: ["complement-de-revenu", "activite-complementaire", "augmenter-ses-revenus"],
    articles: ["creer-deuxieme-source-de-revenus", "10-idees-revenu-complementaire"],
  },

  "activite-complementaire": {
    slug: "activite-complementaire",
    path: "/activite-complementaire",
    breadcrumb: "Activité complémentaire",
    title: "Activité complémentaire : comment en démarrer une en parallèle",
    description:
      "Comment démarrer une activité complémentaire en parallèle de son emploi ? Étapes concrètes, cadre à respecter et idée de revente à domicile. Guide honnête, sans gains garantis.",
    h1: "Activité complémentaire : comment en démarrer une sereinement",
    intro:
      "Une activité complémentaire est une activité menée en plus de votre occupation principale pour générer un revenu supplémentaire. Elle se démarre souvent avec peu de moyens et se développe progressivement. Voici comment vous lancer étape par étape.",
    sections: [
      {
        h: "Choisir la bonne activité complémentaire",
        p: "Privilégiez une activité qui vous plaît et qui se gère par créneaux courts. La revente de produits de beauté est un bon exemple : faible coût de démarrage, pas de local, et un développement à votre rythme.",
      },
      {
        h: "Les étapes pour démarrer",
        list: [
          "Définissez votre objectif et le temps disponible chaque semaine.",
          "Choisissez vos produits ou votre prestation.",
          "Testez auprès de votre entourage pour obtenir vos premiers retours.",
          "Structurez-vous (statut, déclaration) une fois l'activité confirmée.",
        ],
      },
      {
        h: "Le cadre à respecter",
        p: "Vérifiez votre contrat de travail (clauses éventuelles) et vos obligations déclaratives. En France, une micro-entreprise permet souvent de démarrer simplement une activité complémentaire. Renseignez-vous auprès des organismes officiels.",
      },
    ],
    faq: [
      {
        q: "Comment démarrer une activité complémentaire ?",
        a: "Définissez votre objectif, choisissez une activité à faible coût de démarrage, testez auprès de votre entourage, puis structurez-vous (statut, déclaration) une fois l'activité confirmée.",
      },
      {
        q: "Peut-on cumuler une activité complémentaire avec un emploi salarié ?",
        a: "Dans la plupart des cas oui, sous réserve des clauses de votre contrat (exclusivité, non-concurrence) et du respect de vos obligations déclaratives.",
      },
    ],
    related: ["complement-de-revenu", "activite-a-domicile", "revenu-complementaire"],
    articles: ["activite-en-parallele-de-son-emploi", "complement-de-revenu-par-ou-commencer"],
  },

  "activite-a-domicile": {
    slug: "activite-a-domicile",
    path: "/activite-a-domicile",
    breadcrumb: "Activité à domicile",
    title: "Activité à domicile : gagner un revenu complémentaire depuis chez soi",
    description:
      "Quelle activité à domicile pour un revenu complémentaire ? Idées réalistes pour travailler depuis chez soi, le soir ou le week-end, sans promesse de gains garantis.",
    h1: "Activité à domicile : gagner un complément depuis chez soi",
    intro:
      "Une activité à domicile permet de générer un revenu complémentaire sans se déplacer, en s'organisant autour de sa vie personnelle. C'est une solution recherchée par les parents, les étudiants et toute personne disposant de peu de temps. Voici les options les plus accessibles.",
    sections: [
      {
        h: "Pourquoi choisir une activité à domicile ?",
        list: [
          "Travailler depuis chez soi, sans trajet ni local à louer.",
          "S'organiser le soir, le week-end ou pendant les temps libres.",
          "Démarrer avec un investissement limité.",
        ],
      },
      {
        h: "La revente de produits depuis chez soi",
        p: "Avec Ozentya, vous recevez un pack de produits de beauté chez vous. Vous les présentez à votre entourage et sur vos réseaux, puis vous les revendez. Tout se gère à domicile : stockage simple, envois ou remise en main propre, communication en ligne.",
      },
      {
        h: "Bien s'organiser à la maison",
        p: "Réservez un petit espace pour vos produits, planifiez quelques créneaux dédiés dans la semaine et soignez vos photos. Une organisation simple et régulière vaut mieux qu'un grand effort ponctuel.",
      },
    ],
    faq: [
      {
        q: "Quelle activité complémentaire peut-on faire depuis chez soi ?",
        a: "La revente de produits, la création de contenu ou de petites prestations de services se prêtent bien au travail à domicile, car elles se gèrent par créneaux et sans local.",
      },
      {
        q: "Faut-il un gros budget pour une activité à domicile ?",
        a: "Non, certaines activités comme la revente démarrent avec un budget limité (l'achat d'un pack de produits). Les revenus dépendent ensuite des ventes réalisées.",
      },
    ],
    related: ["complement-de-revenu", "activite-complementaire", "arrondir-ses-fins-de-mois"],
    articles: ["activite-en-parallele-de-son-emploi", "trouver-un-complement-de-revenu"],
  },

  "arrondir-ses-fins-de-mois": {
    slug: "arrondir-ses-fins-de-mois",
    path: "/arrondir-ses-fins-de-mois",
    breadcrumb: "Arrondir ses fins de mois",
    title: "Comment arrondir ses fins de mois : idées concrètes et réalistes",
    description:
      "Comment arrondir ses fins de mois sans quitter son emploi ? Idées concrètes, conseils pour démarrer vite et cadre à respecter. Sans promesse d'argent facile.",
    h1: "Comment arrondir ses fins de mois, concrètement",
    intro:
      "Arrondir ses fins de mois, c'est générer un petit revenu supplémentaire pour absorber les dépenses du quotidien ou financer un projet. L'idéal est de choisir une activité flexible, à faible coût de démarrage, que l'on peut mener en parallèle de son emploi. Voici des pistes réalistes.",
    sections: [
      {
        h: "Des idées pour un revenu d'appoint",
        list: [
          "Revendre des produits auprès de son entourage et en ligne.",
          "Proposer de petites prestations ou services selon ses compétences.",
          "Vendre ce dont on ne se sert plus.",
          "Créer du contenu autour d'une passion.",
        ],
      },
      {
        h: "Démarrer vite avec la revente",
        p: "La revente de produits est l'une des pistes les plus accessibles pour arrondir ses fins de mois : coût de démarrage limité, pas de local, premières ventes possibles dès le premier cercle (famille, amis, collègues). Votre gain correspond à la marge entre l'achat et la revente.",
      },
      {
        h: "Rester lucide et honnête",
        p: "Aucune méthode ne garantit un revenu. Fuyez les promesses d'« argent facile » ou de « gains automatiques ». Un revenu d'appoint se construit avec de la régularité et un vrai suivi de ses clients.",
      },
    ],
    faq: [
      {
        q: "Comment arrondir ses fins de mois rapidement ?",
        a: "En partant d'une activité à faible coût et flexible, comme la revente auprès de son entourage. Les premières ventes peuvent arriver vite, mais sans garantie de montant ni de délai.",
      },
      {
        q: "Peut-on arrondir ses fins de mois en travaillant le week-end ?",
        a: "Oui, de nombreuses activités d'appoint se gèrent le soir et le week-end, selon le temps dont vous disposez.",
      },
    ],
    related: ["complement-de-revenu", "activite-a-domicile", "augmenter-ses-revenus"],
    articles: ["augmenter-ses-revenus-mensuels", "10-idees-revenu-complementaire"],
  },

  "augmenter-ses-revenus": {
    slug: "augmenter-ses-revenus",
    path: "/augmenter-ses-revenus",
    breadcrumb: "Augmenter ses revenus",
    title: "Augmenter ses revenus mensuels : méthodes et première étape",
    description:
      "Comment augmenter ses revenus mensuels durablement ? Leviers concrets, création d'une seconde source de revenus et conseils honnêtes, sans gains garantis.",
    h1: "Augmenter ses revenus mensuels : les leviers réalistes",
    intro:
      "Augmenter ses revenus mensuels passe par deux leviers : optimiser son revenu principal (évolution, négociation) ou créer une seconde source de revenus. Cette page se concentre sur la création d'un revenu additionnel, développable à son rythme.",
    sections: [
      {
        h: "Créer une deuxième source de revenus",
        p: "La façon la plus accessible d'augmenter ses revenus est d'ajouter une activité complémentaire. Elle peut rester modeste au début, puis se développer selon le temps que vous y consacrez et les ventes réalisées.",
      },
      {
        h: "La revente comme levier",
        p: "Avec un pack de produits Ozentya, vous disposez d'un stock à présenter et revendre. Chaque vente génère une marge. Plus vous fidélisez de clients, plus ce revenu additionnel devient régulier — sans aucune garantie toutefois.",
      },
      {
        h: "Les bonnes pratiques",
        list: [
          "Fixez-vous un objectif mensuel réaliste.",
          "Suivez vos ventes pour comprendre ce qui fonctionne.",
          "Réinvestissez une partie de vos gains pour développer votre stock.",
          "Restez transparent(e) avec vos clients.",
        ],
      },
    ],
    faq: [
      {
        q: "Comment augmenter ses revenus mensuels ?",
        a: "En créant une seconde source de revenus (activité complémentaire) et/ou en optimisant son revenu principal. La revente de produits est une piste à faible coût de démarrage.",
      },
      {
        q: "Peut-on augmenter ses revenus sans diplôme particulier ?",
        a: "Oui. Une activité de revente ne nécessite pas de diplôme : ce sont la régularité, l'organisation et la relation client qui font la différence.",
      },
    ],
    related: ["complement-de-revenu", "revenu-complementaire", "arrondir-ses-fins-de-mois"],
    articles: ["augmenter-ses-revenus-mensuels", "creer-deuxieme-source-de-revenus"],
  },

  "complement-de-revenu-en-ligne": {
    slug: "complement-de-revenu-en-ligne",
    path: "/complement-de-revenu-en-ligne",
    breadcrumb: "Complément de revenu en ligne",
    title: "Complément de revenu en ligne : les solutions qui marchent (2026)",
    description:
      "Comment gagner un complément de revenu en ligne ? Vente, réseaux sociaux, affiliation, contenu : les options réalistes pour gagner depuis internet, sans gains garantis.",
    h1: "Complément de revenu en ligne : par quoi commencer",
    intro:
      "Gagner un complément de revenu en ligne consiste à utiliser internet (boutique, réseaux sociaux, contenu) pour vendre des produits ou des services au-delà de votre entourage immédiat. L'avantage d'internet est la portée : vous pouvez toucher beaucoup plus de clients potentiels. Les résultats dépendent toutefois de votre régularité et de vos ventes réelles.",
    sections: [
      {
        h: "Pourquoi privilégier le complément de revenu en ligne ?",
        list: [
          "Toucher une audience large, bien au-delà de vos proches.",
          "Travailler depuis chez soi, à vos horaires.",
          "Démarrer souvent avec un budget réduit.",
        ],
      },
      {
        h: "Vendre des produits en ligne",
        p: "La revente de produits se prête parfaitement au digital : vous présentez vos articles en photos et vidéos sur les réseaux sociaux (Instagram, TikTok), et vous vendez à votre communauté. Chez Ozentya, vous combinez vente en ligne et bouche-à-oreille autour de vous.",
      },
      {
        h: "Contenu, affiliation et réseaux",
        p: "Créer du contenu utile autour de votre activité attire une audience qui peut devenir cliente. L'affiliation (recommander des produits avec un lien) complète cette approche, mais demande d'abord une audience fidèle.",
      },
    ],
    faq: [
      {
        q: "Comment gagner un complément de revenu en ligne quand on débute ?",
        a: "Commencez par vendre des produits que vous maîtrisez à votre communauté (réseaux sociaux + entourage), soignez vos photos et publiez régulièrement. Les premières ventes valident votre approche ; aucun montant n'est garanti.",
      },
      {
        q: "Faut-il un site internet pour un complément de revenu en ligne ?",
        a: "Pas forcément au début : les réseaux sociaux et les plateformes suffisent pour démarrer. Un site devient utile plus tard, quand l'activité grandit.",
      },
    ],
    related: ["complement-de-revenu", "activite-a-domicile", "revenu-complementaire"],
    articles: ["10-idees-revenu-complementaire", "trouver-un-complement-de-revenu"],
  },

  "complement-de-revenu-sans-investissement": {
    slug: "complement-de-revenu-sans-investissement",
    path: "/complement-de-revenu-sans-investissement",
    breadcrumb: "Sans investissement",
    title: "Complément de revenu sans investissement ou presque : est-ce possible ?",
    description:
      "Peut-on avoir un complément de revenu sans investissement ou avec un petit budget ? Les options réalistes à faible coût de démarrage, expliquées sans fausses promesses.",
    h1: "Complément de revenu sans investissement : les options réalistes",
    intro:
      "Un complément de revenu « sans investissement » au sens strict est rare : la plupart des activités demandent au minimum du temps, et parfois un petit budget de départ. On peut toutefois commencer avec très peu de moyens. Voici les pistes à faible coût, et comment éviter les pièges de l'« argent facile ».",
    sections: [
      {
        h: "Les activités à coût de démarrage quasi nul",
        list: [
          "Vendre des objets dont on ne se sert plus.",
          "Proposer une compétence (cours, aide, petits services).",
          "Sondages rémunérés et micro-tâches (rentabilité faible).",
          "Créer du contenu autour d'une passion.",
        ],
      },
      {
        h: "Démarrer avec un petit budget",
        p: "Certaines activités, comme la revente de produits, demandent un petit budget initial (l'achat d'un premier stock) mais restent accessibles. L'avantage : vous disposez d'un produit concret à vendre, avec une marge claire entre l'achat et la revente.",
      },
      {
        h: "Attention aux promesses trompeuses",
        p: "Méfiez-vous de toute offre promettant un revenu « sans rien faire » ou « garanti ». Un complément de revenu, même à faible coût, demande du temps et de la régularité. Aucun gain n'est automatique.",
      },
    ],
    faq: [
      {
        q: "Peut-on gagner un complément de revenu sans aucun investissement ?",
        a: "On peut démarrer avec très peu (vendre ses objets, proposer un service), mais cela demande toujours du temps. Les activités totalement « sans effort ni budget » relèvent le plus souvent d'arnaques.",
      },
      {
        q: "Quel petit budget pour se lancer dans la revente ?",
        a: "La revente démarre avec l'achat d'un premier stock de produits. C'est un budget maîtrisé, et votre gain correspond ensuite à la marge réalisée sur vos ventes (non garantie).",
      },
    ],
    related: ["complement-de-revenu", "arrondir-ses-fins-de-mois", "activite-complementaire"],
    articles: ["complement-de-revenu-par-ou-commencer", "10-idees-revenu-complementaire"],
  },

  "revenu-passif": {
    slug: "revenu-passif",
    path: "/revenu-passif",
    breadcrumb: "Revenu passif",
    title: "Revenu passif : mythe ou réalité ? Les vraies options en 2026",
    description:
      "Qu'est-ce qu'un revenu passif et peut-on vraiment en vivre ? Les options réalistes (location, affiliation, royalties) expliquées sans fausses promesses de gains automatiques.",
    h1: "Revenu passif : ce qui est réaliste (et ce qui ne l'est pas)",
    intro:
      "Un revenu passif est un revenu qui demande peu d'effort une fois mis en place — mais « passif » ne veut jamais dire « sans travail ». Presque tous les revenus passifs exigent un investissement initial important : du temps, de l'argent, ou une audience. Voici les options honnêtes, et pourquoi la revente est plutôt un revenu « actif » mais accessible.",
    sections: [
      {
        h: "Qu'est-ce qu'un revenu passif, vraiment ?",
        p: "C'est un revenu qui continue de tomber après un gros effort initial : louer un bien, toucher des royalties, ou percevoir des commissions d'affiliation. L'effort est surtout au départ ; l'entretien reste léger ensuite.",
      },
      {
        h: "Les options réalistes",
        list: [
          "Location (logement, voiture, matériel) : nécessite de posséder un bien.",
          "Affiliation / contenu : nécessite une audience construite au préalable.",
          "Produits numériques, royalties : nécessitent une création initiale.",
        ],
      },
      {
        h: "Revente : un revenu actif mais accessible",
        p: "La revente de produits n'est pas un revenu passif : elle demande une activité régulière. En revanche, son coût de démarrage est faible et vous contrôlez votre rythme — un bon compromis quand on n'a ni bien à louer ni audience établie.",
      },
    ],
    faq: [
      {
        q: "Peut-on vivre d'un revenu passif ?",
        a: "C'est rare et long à construire. La plupart des gens combinent plusieurs sources (actives et passives). Méfiez-vous des promesses de revenu passif « automatique » : elles sont généralement trompeuses.",
      },
      {
        q: "Quel revenu passif demande le moins d'argent au départ ?",
        a: "L'affiliation et les produits numériques demandent peu d'argent mais beaucoup de temps (créer une audience/un contenu). La location demande un bien. Aucun n'est réellement « gratuit ».",
      },
    ],
    related: ["complement-de-revenu", "augmenter-ses-revenus", "complement-de-revenu-en-ligne"],
    articles: ["creer-deuxieme-source-de-revenus", "10-idees-revenu-complementaire"],
  },

  "complement-de-revenu-le-soir-et-week-end": {
    slug: "complement-de-revenu-le-soir-et-week-end",
    path: "/complement-de-revenu-le-soir-et-week-end",
    breadcrumb: "Le soir & le week-end",
    title: "Complément de revenu le soir et le week-end : idées compatibles emploi",
    description:
      "Quel complément de revenu faire le soir ou le week-end quand on travaille la journée ? Idées flexibles, compatibles avec un emploi, sans promesse de gains.",
    h1: "Complément de revenu le soir et le week-end",
    intro:
      "Quand on a un emploi à temps plein, le complément de revenu se développe sur les créneaux libres : le soir et le week-end. L'enjeu est de choisir une activité flexible, qu'on peut mettre en pause et reprendre sans contrainte d'horaires fixes. Voici les options les plus adaptées.",
    sections: [
      {
        h: "Pourquoi les créneaux soir/week-end ?",
        p: "Ce sont les moments où vous êtes disponible sans empiéter sur votre emploi. Une activité gérée par petits créneaux (plutôt qu'à horaires imposés) s'y intègre naturellement.",
      },
      {
        h: "Les activités qui s'y prêtent le mieux",
        list: [
          "Revente de produits : présentation et envois quand vous voulez.",
          "Prestations ponctuelles (cours, aide) le week-end.",
          "Création de contenu publiée le soir.",
          "Vente d'objets d'occasion sur votre temps libre.",
        ],
      },
      {
        h: "Garder un équilibre",
        p: "Travailler en plus de son emploi doit rester soutenable. Fixez-vous des créneaux réalistes (par exemple 2 soirs + le samedi matin) pour préserver votre repos.",
      },
    ],
    faq: [
      {
        q: "Quel complément de revenu faire le week-end ?",
        a: "La revente, les petites prestations et la vente d'objets sont idéales le week-end car elles se gèrent librement, sans horaires imposés.",
      },
      {
        q: "Peut-on gagner un complément de revenu en travaillant seulement le soir ?",
        a: "Oui, en choisissant une activité flexible. Les montants dépendent du temps réellement consacré et des ventes réalisées ; rien n'est garanti.",
      },
    ],
    related: ["complement-de-revenu", "activite-complementaire", "arrondir-ses-fins-de-mois"],
    articles: ["activite-en-parallele-de-son-emploi", "complement-de-revenu-par-ou-commencer"],
  },

  "complement-de-revenu-salarie": {
    slug: "complement-de-revenu-salarie",
    path: "/complement-de-revenu-salarie",
    breadcrumb: "Pour salarié",
    title: "Complément de revenu pour salarié : cadre, statut et idées",
    description:
      "Complément de revenu quand on est salarié : ce que dit votre contrat, le statut à choisir et les activités compatibles. Guide clair pour la France, sans gains garantis.",
    h1: "Complément de revenu pour salarié : ce qu'il faut savoir",
    intro:
      "En tant que salarié, vous pouvez développer un complément de revenu en parallèle de votre emploi, à condition de respecter votre contrat de travail et vos obligations déclaratives. Voici le cadre à connaître et les activités les plus adaptées à un emploi du temps de salarié.",
    sections: [
      {
        h: "Ce que dit votre contrat de travail",
        p: "Vérifiez les clauses d'exclusivité et de non-concurrence. La plupart des salariés peuvent exercer une activité annexe tant qu'elle ne concurrence pas l'employeur et ne se fait pas sur le temps de travail. En cas de doute, renseignez-vous auprès des services compétents.",
      },
      {
        h: "Le statut adapté",
        p: "En France, le statut de micro-entrepreneur permet de cumuler simplement un emploi salarié et une activité indépendante déclarée. Vous déclarez votre chiffre d'affaires et payez les cotisations correspondantes.",
      },
      {
        h: "Les activités compatibles",
        p: "Privilégiez une activité flexible, gérable le soir et le week-end : la revente de produits en est un bon exemple, car elle n'impose aucun horaire fixe.",
      },
    ],
    faq: [
      {
        q: "Un salarié a-t-il le droit d'avoir un complément de revenu ?",
        a: "Oui dans la majorité des cas, sous réserve des clauses de son contrat (exclusivité, non-concurrence) et du respect de ses obligations déclaratives.",
      },
      {
        q: "Faut-il déclarer un complément de revenu quand on est salarié ?",
        a: "Oui. Les revenus d'une activité indépendante doivent être déclarés. Le statut de micro-entrepreneur simplifie ces démarches en France.",
      },
    ],
    related: ["complement-de-revenu", "activite-complementaire", "complement-de-revenu-le-soir-et-week-end"],
    articles: ["activite-en-parallele-de-son-emploi", "complement-de-revenu-par-ou-commencer"],
  },

  "complement-de-revenu-independant": {
    slug: "complement-de-revenu-independant",
    path: "/complement-de-revenu-independant",
    breadcrumb: "Pour indépendant",
    title: "Complément de revenu pour indépendant : diversifier ses sources",
    description:
      "Indépendant ou micro-entrepreneur : comment ajouter un complément de revenu pour diversifier et sécuriser son activité ? Pistes concrètes, sans promesse de gains.",
    h1: "Complément de revenu pour indépendant : diversifier ses revenus",
    intro:
      "Quand on est déjà indépendant (freelance, artisan, micro-entrepreneur), un complément de revenu permet de lisser les périodes creuses et de ne pas dépendre d'une seule activité. L'objectif : ajouter une source complémentaire qui s'intègre à votre organisation existante.",
    sections: [
      {
        h: "Pourquoi diversifier quand on est indépendant ?",
        p: "Un indépendant subit des variations d'activité. Une seconde source de revenus réduit cette dépendance et sécurise votre budget pendant les mois plus calmes.",
      },
      {
        h: "Ajouter la revente à son activité",
        p: "La revente de produits se combine facilement avec une activité indépendante : vous l'intégrez à votre offre existante ou la proposez à votre clientèle actuelle. Votre marge vient de la différence entre l'achat et la revente.",
      },
      {
        h: "Le cadre pour un indépendant",
        p: "Vérifiez que votre nouvelle activité entre dans votre statut (plafonds de la micro-entreprise, catégorie vente vs service) et déclarez l'ensemble de vos revenus. Un expert-comptable peut vous aider à optimiser.",
      },
    ],
    faq: [
      {
        q: "Un micro-entrepreneur peut-il ajouter une activité de revente ?",
        a: "Oui, en respectant les plafonds de chiffre d'affaires et en déclarant la bonne catégorie d'activité (vente de marchandises). Renseignez-vous sur l'URSSAF.",
      },
      {
        q: "Pourquoi diversifier ses sources de revenus quand on est freelance ?",
        a: "Pour réduire la dépendance à une seule activité et sécuriser ses revenus pendant les périodes creuses.",
      },
    ],
    related: ["complement-de-revenu", "augmenter-ses-revenus", "revenu-passif"],
    articles: ["creer-deuxieme-source-de-revenus", "augmenter-ses-revenus-mensuels"],
  },
};

export const getResourcePage = (slug) => RESOURCE_PAGES[slug];
export const RESOURCE_LIST = Object.values(RESOURCE_PAGES);
